import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { tokens } from "@/tokens";
import { tableCompactSx } from "@/components/table/table-styles";
import type { IeepaAnalysisEntry, IeepaBucketKey } from "@/types/ieepa";

type EntryTableVariant = "default" | "certified" | "pending";

interface EntryTableProps {
  rows: IeepaAnalysisEntry[];
  emptyMessage?: string;
  /** Initial page size. Defaults to 50. */
  initialRowsPerPage?: number;
  /** Selectable page sizes in the footer dropdown. */
  rowsPerPageOptions?: number[];
  /**
   * Column set to render.
   *  - "default"   = bucket-classification view for Eligible /
   *                  Protest Recommended / Out of Window.
   *  - "certified" = refund-payload view for the CAPE Certified tab
   *                  (refund $, date, claim #, status, interest).
   *  - "pending"   = projection view for the CAPE Pending tab
   *                  (paid $, expected refund, claim # + status, days since liq).
   */
  variant?: EntryTableVariant;
}

const bucketTone = (
  bucket: IeepaBucketKey
): { bg: string; text: string } => {
  if (bucket === "CAPE")
    return { bg: `${tokens.status.success}15`, text: tokens.status.success };
  if (bucket === "PROTEST_RECOMMENDED")
    return { bg: `${tokens.status.warning}15`, text: tokens.status.warning };
  if (bucket === "OUT_OF_WINDOW")
    return { bg: `${tokens.status.danger}15`, text: tokens.status.danger };
  return { bg: tokens.bg.muted, text: tokens.text.muted };
};

const bucketLabel = (bucket: IeepaBucketKey): string => {
  if (bucket === "PROTEST_RECOMMENDED") return "Protest Recommended";
  if (bucket === "OUT_OF_WINDOW") return "Out of Window";
  return bucket;
};

const fmtMoney = (v: number): string =>
  `$${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtDate = (v: string | null): string => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? "—"
    : `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
        d.getDate()
      ).padStart(2, "0")}/${d.getFullYear()}`;
};

export const EntryTable: React.FC<EntryTableProps> = ({
  rows,
  emptyMessage,
  initialRowsPerPage = 50,
  rowsPerPageOptions = [25, 50, 100, 250],
  variant = "default",
}) => {
  const isCertified = variant === "certified";
  const isPending = variant === "pending";

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Reset to page 0 any time the underlying row set changes (toggle flip,
  // tab switch reusing this component, etc.) so the user never lands on a
  // now-empty page.
  useEffect(() => {
    setPage(0);
  }, [rows]);

  const pageRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${tokens.border.container}`,
          borderRadius: tokens.radius.lg,
          bgcolor: tokens.bg.surface,
          boxShadow: tokens.shadow.sm,
          color: tokens.text.muted,
          fontSize: tokens.fontSize.sm2,
          py: 6,
          textAlign: "center",
        }}
      >
        {emptyMessage || "No entries in this bucket."}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${tokens.border.container}`,
        borderRadius: tokens.radius.lg,
        bgcolor: tokens.bg.surface,
        boxShadow: tokens.shadow.sm,
        overflow: "hidden",
      }}
    >
    <TableContainer
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        ...tableCompactSx,
      }}
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          minWidth: 1280,
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: tokens.bg.hover,
            color: tokens.text.secondary,
            fontSize: tokens.fontSize.xs,
            fontWeight: tokens.fontWeight.semibold,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            borderBottom: `1px solid ${tokens.border.default}`,
            whiteSpace: "nowrap",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Entry Summary #</TableCell>
            <TableCell>IOR</TableCell>
            <TableCell>Importer</TableCell>
            <TableCell>Entry Date</TableCell>
            <TableCell>Liq. Date</TableCell>
            {isCertified ? (
              <>
                <TableCell>IEEPA Authority</TableCell>
                <TableCell align="right">IEEPA $</TableCell>
                <TableCell align="right">Refund $</TableCell>
                <TableCell align="right">Interest $</TableCell>
                <TableCell>Refund Date</TableCell>
                <TableCell>Claim #</TableCell>
                <TableCell>Status</TableCell>
              </>
            ) : isPending ? (
              <>
                <TableCell align="right">Days Since Liq.</TableCell>
                <TableCell>IEEPA Authority</TableCell>
                <TableCell align="right">IEEPA $</TableCell>
                <TableCell align="right">Paid $</TableCell>
                <TableCell align="right">Expected Refund</TableCell>
                <TableCell>Claim #</TableCell>
                <TableCell>Claim Status</TableCell>
                <TableCell>Recon</TableCell>
                <TableCell>Protest</TableCell>
              </>
            ) : (
              <>
                <TableCell>Liq. Status</TableCell>
                <TableCell align="right">Days Since Liq.</TableCell>
                <TableCell>Filer</TableCell>
                <TableCell>IEEPA Authority</TableCell>
                <TableCell align="right">IEEPA $</TableCell>
                <TableCell>Bucket</TableCell>
                <TableCell>Recon</TableCell>
                <TableCell>Protest</TableCell>
                <TableCell sx={{ minWidth: 260 }}>Notes</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {pageRows.map((r) => {
            const tone = bucketTone(r.bucket);
            return (
              <TableRow
                key={r.entrySummaryNumber}
                hover
                sx={{ "& td": { fontSize: tokens.fontSize.sm2 } }}
              >
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {r.entrySummaryNumber}
                </TableCell>
                <TableCell>{r.importerNumber || "—"}</TableCell>
                <TableCell>{r.importerName || "—"}</TableCell>
                <TableCell>{fmtDate(r.entryDate)}</TableCell>
                <TableCell>{fmtDate(r.liquidationDate)}</TableCell>
                {isCertified ? (
                  <>
                    <TableCell>{r.ieepaAuthorityLabel || "—"}</TableCell>
                    <TableCell align="right">{fmtMoney(r.ieepaDuty)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: tokens.fontWeight.semibold,
                        color: tokens.status.success,
                      }}
                    >
                      {fmtMoney(r.refundAmount || 0)}
                    </TableCell>
                    <TableCell align="right">
                      {r.refundInterestTotalAmount > 0
                        ? fmtMoney(r.refundInterestTotalAmount)
                        : "—"}
                    </TableCell>
                    <TableCell>{fmtDate(r.refundDate)}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {r.claimNumber || "—"}
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          bgcolor: `${tokens.status.success}15`,
                          color: tokens.status.success,
                          fontWeight: tokens.fontWeight.semibold,
                          fontSize: "10px",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          px: 1,
                          py: 0.25,
                          borderRadius: "3px",
                        }}
                      >
                        {r.refundStatus || r.claimStatus || "Certified"}
                      </Box>
                    </TableCell>
                  </>
                ) : isPending ? (
                  <>
                    <TableCell align="right">
                      {r.daysSinceLiquidation ?? "—"}
                    </TableCell>
                    <TableCell>{r.ieepaAuthorityLabel || "—"}</TableCell>
                    <TableCell align="right">{fmtMoney(r.ieepaDuty)}</TableCell>
                    <TableCell align="right">
                      {r.totalPaidDuty > 0 ? fmtMoney(r.totalPaidDuty) : "—"}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: tokens.fontWeight.semibold,
                        color: tokens.status.success,
                      }}
                    >
                      {fmtMoney(r.expectedRefund || 0)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {r.claimNumber || "—"}
                    </TableCell>
                    <TableCell>
                      {r.claimStatus ? (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            bgcolor: `${tokens.brand.primary}15`,
                            color: tokens.brand.primary,
                            fontWeight: tokens.fontWeight.semibold,
                            fontSize: "10px",
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            px: 1,
                            py: 0.25,
                            borderRadius: "3px",
                          }}
                        >
                          {r.claimStatus}
                        </Box>
                      ) : (
                        <Typography
                          component="span"
                          sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.sm2 }}
                        >
                          No claim filed
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.reconciliationIndicator === "Y" ? (
                        <Typography
                          component="span"
                          sx={{
                            color: tokens.status.warning,
                            fontWeight: tokens.fontWeight.semibold,
                            fontSize: tokens.fontSize.sm2,
                          }}
                        >
                          Y
                        </Typography>
                      ) : (
                        <Typography
                          component="span"
                          sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.sm2 }}
                        >
                          N
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.hasExistingProtest ? (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            component="span"
                            sx={{
                              color: tokens.status.danger,
                              fontWeight: tokens.fontWeight.semibold,
                              fontSize: "10px",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Existing
                          </Typography>
                          {r.protestNumber && (
                            <Typography
                              component="span"
                              sx={{
                                color: tokens.text.primary,
                                fontSize: tokens.fontSize.sm2,
                                fontFamily: "monospace",
                              }}
                            >
                              {r.protestNumber}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography
                          component="span"
                          sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.sm2 }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{r.liquidationStatus || "—"}</TableCell>
                    <TableCell align="right">
                      {r.daysSinceLiquidation ?? "—"}
                    </TableCell>
                    <TableCell>{r.filerCode || "—"}</TableCell>
                    <TableCell>{r.ieepaAuthorityLabel || "—"}</TableCell>
                    <TableCell align="right">{fmtMoney(r.ieepaDuty)}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          bgcolor: tone.bg,
                          color: tone.text,
                          fontWeight: tokens.fontWeight.semibold,
                          fontSize: "10px",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          px: 1,
                          py: 0.25,
                          borderRadius: "3px",
                        }}
                      >
                        {bucketLabel(r.bucket)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {r.reconciliationIndicator === "Y" ? (
                        <Typography
                          component="span"
                          sx={{
                            color: tokens.status.warning,
                            fontWeight: tokens.fontWeight.semibold,
                            fontSize: tokens.fontSize.sm2,
                          }}
                        >
                          Y
                        </Typography>
                      ) : (
                        <Typography
                          component="span"
                          sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.sm2 }}
                        >
                          N
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.hasExistingProtest ? (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            component="span"
                            sx={{
                              color: tokens.status.danger,
                              fontWeight: tokens.fontWeight.semibold,
                              fontSize: "10px",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Existing
                          </Typography>
                          {r.protestNumber && (
                            <Typography
                              component="span"
                              sx={{
                                color: tokens.text.primary,
                                fontSize: tokens.fontSize.sm2,
                                fontFamily: "monospace",
                              }}
                            >
                              {r.protestNumber}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography
                          component="span"
                          sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.sm2 }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: tokens.fontSize.sm2,
                        color: tokens.text.muted,
                        whiteSpace: "normal",
                      }}
                    >
                      {r.eligibilityNotes || ""}
                    </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={rowsPerPageOptions}
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.surface,
          "& .MuiTablePagination-toolbar": { minHeight: 44 },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              fontSize: tokens.fontSize.sm2,
              color: tokens.text.muted,
            },
        }}
      />
    </Box>
  );
};
